import type { Snapshot } from "./Rollback";
import type { Rollback } from "./Rollback";
import type { EventBus } from "./EventBus";
import type { Interpreter } from "./Interpreter";

const SLOT_IDS = ["auto", "slot1", "slot2", "slot3", "slot4", "slot5"] as const;
export type SlotId = (typeof SLOT_IDS)[number];

export interface SaveMeta {
  slotId: SlotId;
  timestamp: number;
  sceneName: string;
  dialoguePreview: string;
  programVersion: string;
  schemaVersion: 1;
}

export interface SaveData {
  meta: SaveMeta;
  snapshot: Snapshot;
}

export interface SaveStorage {
  get(key: string): Promise<SaveData | null>;
  set(key: string, data: SaveData): Promise<void>;
  delete(key: string): Promise<void>;
  list(): Promise<SaveMeta[]>;
}

const DB_NAME = "browser_vn_saves";
const DB_VERSION = 1;
const STORE_NAME = "saves";

export class IDBSaveStorage implements SaveStorage {
  private _dbPromise: Promise<IDBDatabase> | null = null;

  private _openDB(): Promise<IDBDatabase> {
    if (this._dbPromise) return this._dbPromise;
    this._dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return this._dbPromise;
  }

  async get(key: string): Promise<SaveData | null> {
    const db = await this._openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => resolve((req.result as SaveData | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
  }

  async set(key: string, data: SaveData): Promise<void> {
    const db = await this._openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(data, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async delete(key: string): Promise<void> {
    const db = await this._openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async list(): Promise<SaveMeta[]> {
    const db = await this._openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => {
        const results = req.result as SaveData[];
        resolve(results.map((r) => r.meta));
      };
      req.onerror = () => reject(req.error);
    });
  }
}

export class SaveLoad {
  private _unsubs: Array<() => void> = [];
  private _autosavePending = false;
  private _lastAutosaveScene = "";
  private _lastDialogue = "";

  constructor(
    private _rollback: Rollback,
    private _interpreter: Interpreter,
    private _bus: EventBus,
    private _storage: SaveStorage,
  ) {
    this._unsubs.push(
      _bus.on("dialogue.narrate", (d) => {
        this._lastDialogue = d.resolved;
      }),
      _bus.on("dialogue.say", (d) => {
        this._lastDialogue = d.resolved;
      }),
      _bus.on("choice.select", () => {
        this._autosavePending = true;
      }),
      _bus.on("scene.load", () => {
        this._autosavePending = true;
      }),
      _bus.on("rollback.snapshot", () => {
        if (this._autosavePending) {
          this._autosavePending = false;
          this._triggerAutosave();
        }
        this._checkChkAutosave();
      }),
    );
  }

  async save(slotId: SlotId): Promise<boolean> {
    const snapshot = this._rollback.getLatestSnapshot();
    if (!snapshot) return false;

    const programVersion = this._interpreter.getProgram()?.version ?? "unknown";

    const meta: SaveMeta = {
      slotId,
      timestamp: Date.now(),
      sceneName: snapshot.scene,
      dialoguePreview: this._lastDialogue,
      programVersion,
      schemaVersion: 1,
    };

    await this._storage.set(slotId, { meta, snapshot: structuredClone(snapshot) });
    this._bus.emit("save.done", { slotId });
    return true;
  }

  async load(slotId: SlotId): Promise<boolean> {
    const data = await this._storage.get(slotId);
    if (!data) return false;

    const currentVersion = this._interpreter.getProgram()?.version ?? "unknown";
    if (data.meta.programVersion !== currentVersion) {
      console.warn(
        `SaveLoad: program version mismatch (save: ${data.meta.programVersion}, current: ${currentVersion})`,
      );
      this._bus.emit("save.versionMismatch", {
        slotId,
        saveVersion: data.meta.programVersion,
        currentVersion,
      });
    }

    this._rollback.restoreFromSave(data.snapshot);
    this._bus.emit("load.done", { slotId });
    return true;
  }

  async listSlots(): Promise<SaveMeta[]> {
    return this._storage.list();
  }

  async deleteSlot(slotId: SlotId): Promise<void> {
    await this._storage.delete(slotId);
  }

  private _triggerAutosave(): void {
    this.save("auto").catch((err) => {
      console.warn("SaveLoad: autosave failed", err);
    });
  }

  private _checkChkAutosave(): void {
    const scene = this._interpreter.currentScene;
    if (scene.startsWith("chk_") && scene !== this._lastAutosaveScene) {
      this._lastAutosaveScene = scene;
      this._triggerAutosave();
    }
  }

  shutdown(): void {
    for (const unsub of this._unsubs) unsub();
    this._unsubs = [];
  }
}
